import { useEffect } from 'react'
import {
  CloseButton,
  Button,
  Input,
  NativeSelect,
  Textarea,
  VStack,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useCreateLobbyDayEvent, useUpdateLobbyDayEvent } from '../../hooks/useLobbyDay'
import { useAvailableStates } from '../../hooks/useAvailableStates'
import type { LobbyDayEventDetailDto, SaveLobbyDayEventRequest } from '../../types/lobbyDay'

interface EventFormModalProps {
  isOpen: boolean
  onClose: () => void
  event?: LobbyDayEventDetailDto
}

export function EventFormModal({ isOpen, onClose, event }: EventFormModalProps) {
  const navigate = useNavigate()
  const createMutation = useCreateLobbyDayEvent()
  const updateMutation = useUpdateLobbyDayEvent(event?.id ?? '')
  const isEdit = !!event

  const { data: states = [] } = useAvailableStates()

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<SaveLobbyDayEventRequest>({
      defaultValues: { name: '', location: '', description: '', startDate: '', endDate: '', status: 'Active', state: '', level: 'State' },
    })

  useEffect(() => {
    if (isOpen) {
      reset(event
        ? { name: event.name, location: event.location ?? '', description: event.description ?? '',
            startDate: event.startDate, endDate: event.endDate ?? '', status: event.status,
            state: event.state ?? (states.length === 1 ? states[0].code : ''), level: event.level ?? 'State' }
        : { name: '', location: '', description: '', startDate: '', endDate: '', status: 'Active',
            state: states.length === 1 ? states[0].code : '', level: 'State' })
    }
    // Re-run when the granted-states list resolves (async query) so create mode can seed the
    // single-granted-state default once it arrives.
  }, [isOpen, event, reset, states])

  const onSubmit = handleSubmit(async (values) => {
    const payload: SaveLobbyDayEventRequest = {
      ...values,
      location: values.location || null,
      description: values.description || null,
      endDate: values.endDate || null,
    }
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(payload)
        toaster.create({ type: 'success', title: 'Event updated' })
      } else {
        const created = await createMutation.mutateAsync(payload)
        toaster.create({ type: 'success', title: 'Event created' })
        navigate(`/dashboard/lobby-day/events/${created.id}`)
      }
      onClose()
    } catch {
      toaster.create({ type: 'error', title: isEdit ? 'Failed to update event' : 'Failed to create event' })
    }
  })

  return (
    <Dialog.Root open={isOpen} size='lg' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content asChild><form onSubmit={onSubmit}>
              <Dialog.Header>{isEdit ? 'Edit Event' : 'New Lobby Day Event'}</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
              <Dialog.Body>
                <VStack gap={4}>
                  <Field.Root invalid={!!errors.name} required>
                    <Field.Label>Name</Field.Label>
                    <Input {...register('name', { required: 'Name is required' })} placeholder="Nurses Lobby Day 2026" />
                    <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
                  </Field.Root>
                  <Field.Root invalid={!!errors.startDate} required>
                    <Field.Label>Start date</Field.Label>
                    <Input type="date" {...register('startDate', { required: 'Start date is required' })} />
                    <Field.ErrorText>{errors.startDate?.message}</Field.ErrorText>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>End date (multi-day only)</Field.Label>
                    <Input type="date" {...register('endDate')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Location</Field.Label>
                    <Input {...register('location')} placeholder="State Capitol, St. Paul" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea {...register('description')} rows={3} />
                  </Field.Root>
                  <Field.Root required invalid={!!errors.state}>
                    <Field.Label>State</Field.Label>
                    {states.length === 1 ? (
                      <>
                        <Input value={states[0].name} readOnly bg="surface.2" />
                        <input type="hidden" {...register('state', { value: states[0].code })} />
                      </>
                    ) : (
                      <NativeSelect.Root>
                        <NativeSelect.Field {...register('state', { required: 'State is required' })}>
                          <option value="" disabled>Select a state…</option>
                          {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    )}
                    <Field.ErrorText>{errors.state?.message}</Field.ErrorText>
                  </Field.Root>
                  <Field.Root required>
                    <Field.Label>Level</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field {...register('level', { required: true })}>
                        <option value="State">State legislature</option>
                        <option value="Federal">US Congress</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  {isEdit && (
                    <Field.Root>
                      <Field.Label>Status</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field {...register('status')}>
                          <option value="Draft">Draft — hold attendee links</option>
                          <option value="Active">Active (attendee links live)</option>
                          <option value="Archived">Archived</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                <Button colorPalette="brand" type="submit" loading={isSubmitting}>
                  {isEdit ? 'Save' : 'Create'}
                </Button>
              </Dialog.Footer>
            </form></Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
